import React from 'react'
import { Card } from 'antd'
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from 'recharts'
import { useTranslation } from 'react-i18next'

import { BaseChartContainer } from '../BaseChartContainer/BaseChartContainer'
import { ChartTheme } from '../../../utils/chartTheme'
import {
    getMosquitoCategoryDisplayName,
    normalizeConfiguredDisplayCategory,
    sortMosquitoDisplayCategories,
    type MosquitoDisplayCategory,
} from '../../../utils/mosquitoDisplay'

interface DistributionChartProps {
    data: {
        name: string
        value: number
    }[]
}

export const DistributionChart: React.FC<DistributionChartProps> = ({
    data,
}) => {
    const { t } = useTranslation('statistics')
    const counts = data.reduce<Partial<Record<MosquitoDisplayCategory, number>>>(
        (accumulator, item) => {
            const category = normalizeConfiguredDisplayCategory(item.name)
            if (category) {
                accumulator[category] = (accumulator[category] || 0) + item.value
            }
            return accumulator
        },
        {}
    )
    const total = Object.values(counts).reduce<number>(
        (sum, value) => sum + (value ?? 0),
        0
    )
    const chartData = sortMosquitoDisplayCategories(
        Object.keys(counts) as MosquitoDisplayCategory[]
    ).map((category) => ({
        originalName: category,
        name: getMosquitoCategoryDisplayName(category, t, 'pestTypes'),
        value: counts[category] || 0,
        percent: total > 0 ? (counts[category] || 0) / total : 0,
    }))
    const csvHeaders = [
        t('charts.distribution.csv.name'),
        t('charts.distribution.csv.value'),
        t('charts.distribution.csv.percent'),
    ]
    const csvData = chartData.map((item) => ({
        [csvHeaders[0]]: item.name,
        [csvHeaders[1]]: item.value,
        [csvHeaders[2]]: item.percent,
    }))
    return (
        <Card>
            <BaseChartContainer
                title={t('charts.distribution.title')}
                tooltip={t('charts.distribution.tooltip')}
                containerSelector=".distribution-chart-container"
                fileNamePrefix={t('charts.distribution.fileNamePrefix')}
                rawData={csvData}
                csvHeaders={csvHeaders}
                showCsvExport={false}
            >
                <ResponsiveContainer>
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) =>
                                `${name}: ${(percent * 100).toFixed(1)}%`
                            }
                            labelLine
                            isAnimationActive
                            animationBegin={0}
                            animationDuration={1200}
                            animationEasing="ease-out"
                        >
                            {chartData.map((item, index) => (
                                <Cell
                                    key={`cell-${item.originalName}`}
                                    fill={
                                        ChartTheme.colors.primary[
                                            index % ChartTheme.colors.primary.length
                                        ]
                                    }
                                />
                            ))}
                        </Pie>
                        <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                        <RechartsTooltip
                            formatter={(value, name, props) => {
                                const percent = (props.payload.percent * 100).toFixed(1)
                                return [`${value} (${percent}%)`, name]
                            }}
                            contentStyle={ChartTheme.tooltipStyle}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </BaseChartContainer>

            <div style={{ textAlign: 'center', marginTop: 10 }}>
                <div style={{ fontSize: '16px' }}>
                    {t('charts.distribution.pestTypeCount')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {chartData.length}
                </div>
            </div>
        </Card>
    )
}
